import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, GraduationCap, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast({
        title: 'Invalid Reset Link',
        description: 'The password reset link is invalid or has expired.',
        variant: 'destructive'
      });
      navigate('/login');
    }
  }, [token, navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    try {
      setIsLoading(true);

      // Call the reset password API
      const { error } = await window.ezsite.apis.resetPassword({
        token: token!,
        password: password
      });

      if (error) {
        throw new Error(error);
      }

      toast({
        title: 'Password Reset Successful',
        description: 'Your password has been reset successfully. You can now log in with your new password.'
      });

      navigate('/login');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4" data-id="vgtm0j9fu" data-path="src/pages/ResetPassword.tsx">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md" data-id="191woobdc" data-path="src/pages/ResetPassword.tsx">

        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm" data-id="yuuiveurc" data-path="src/pages/ResetPassword.tsx">
          <CardHeader className="text-center pb-6" data-id="j7waginz8" data-path="src/pages/ResetPassword.tsx">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="flex justify-center mb-4" data-id="ltev1e1ra" data-path="src/pages/ResetPassword.tsx">

              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full" data-id="ofdtda13a" data-path="src/pages/ResetPassword.tsx">
                <GraduationCap className="h-8 w-8 text-white" data-id="zne2izm5e" data-path="src/pages/ResetPassword.tsx" />
              </div>
            </motion.div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="3flp4onza" data-path="src/pages/ResetPassword.tsx">
              Reset Password
            </CardTitle>
          </CardHeader>
          
          <form onSubmit={handleSubmit} data-id="swvrbgz7n" data-path="src/pages/ResetPassword.tsx">
            <CardContent className="space-y-4" data-id="zvd499qon" data-path="src/pages/ResetPassword.tsx">
              {error &&
              <Alert variant="destructive" data-id="mshtnjjgx" data-path="src/pages/ResetPassword.tsx">
                  <AlertDescription data-id="8zb2v5izx" data-path="src/pages/ResetPassword.tsx">{error}</AlertDescription>
                </Alert>
              }
              
              <div className="space-y-2" data-id="uvyv1tufi" data-path="src/pages/ResetPassword.tsx">
                <Label htmlFor="password" className="text-sm font-medium" data-id="eif8ccuqp" data-path="src/pages/ResetPassword.tsx">
                  New Password
                </Label>
                <div className="relative" data-id="gkvu8ngkx" data-path="src/pages/ResetPassword.tsx">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="73nuqfz9q" data-path="src/pages/ResetPassword.tsx" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6} data-id="16s7ssw36" data-path="src/pages/ResetPassword.tsx" />

                </div>
              </div>
              
              <div className="space-y-2" data-id="5t2blzs94" data-path="src/pages/ResetPassword.tsx">
                <Label htmlFor="confirmPassword" className="text-sm font-medium" data-id="6fvjgzf5q" data-path="src/pages/ResetPassword.tsx">
                  Confirm Password
                </Label>
                <div className="relative" data-id="1xnqcp0o5" data-path="src/pages/ResetPassword.tsx">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" data-id="x1sopv4ry" data-path="src/pages/ResetPassword.tsx" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6} data-id="9xp5zfjkb" data-path="src/pages/ResetPassword.tsx" />

                </div>
              </div>
            </CardContent>
            
            <CardFooter data-id="a11fxc85v" data-path="src/pages/ResetPassword.tsx">
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                disabled={isLoading} data-id="6bal2sb3f" data-path="src/pages/ResetPassword.tsx">

                {isLoading ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="vsuhrgpqq" data-path="src/pages/ResetPassword.tsx" />
                    Resetting Password...
                  </> :

                'Reset Password'
                }
              </Button>
            </CardFooter>
          </form>
        </Card>
      </motion.div>
    </div>);

};

export default ResetPassword;